const DAY_MS = 24 * 60 * 60 * 1000;

function dateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function nthMonday(year: number, month: number, occurrence: number): number {
  const firstDay = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const firstMonday = 1 + ((8 - firstDay) % 7);
  return firstMonday + (occurrence - 1) * 7;
}

function vernalEquinoxDay(year: number): number {
  return Math.floor(20.8431 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
}

function autumnalEquinoxDay(year: number): number {
  return Math.floor(23.2488 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
}

function addBaseHolidays(year: number, holidays: Set<string>): void {
  const add = (month: number, day: number) => holidays.add(dateKey(year, month, day));

  add(1, 1);
  add(1, nthMonday(year, 1, 2));
  add(2, 11);
  if (year >= 2020) add(2, 23);
  add(3, vernalEquinoxDay(year));
  add(4, 29);
  add(5, 3);
  add(5, 4);
  add(5, 5);

  if (year === 2020) {
    add(7, 23);
    add(7, 24);
    add(8, 10);
  } else if (year === 2021) {
    add(7, 22);
    add(7, 23);
    add(8, 8);
  } else {
    add(7, nthMonday(year, 7, 3));
    add(8, 11);
    add(10, nthMonday(year, 10, 2));
  }

  add(9, nthMonday(year, 9, 3));
  add(9, autumnalEquinoxDay(year));
  add(11, 3);
  add(11, 23);
}

function buildHolidaySet(year: number): Set<string> {
  const holidays = new Set<string>();
  addBaseHolidays(year, holidays);

  // 国民の祝日に挟まれた平日も休日になる（例: 2026-09-22）。
  const yearStart = Date.UTC(year, 0, 1);
  const yearEnd = Date.UTC(year + 1, 0, 1);
  for (let time = yearStart + DAY_MS; time < yearEnd - DAY_MS; time += DAY_MS) {
    const current = new Date(time);
    const currentKey = dateKey(year, current.getUTCMonth() + 1, current.getUTCDate());
    const previous = new Date(time - DAY_MS);
    const next = new Date(time + DAY_MS);
    const previousKey = dateKey(year, previous.getUTCMonth() + 1, previous.getUTCDate());
    const nextKey = dateKey(year, next.getUTCMonth() + 1, next.getUTCDate());
    if (!holidays.has(currentKey) && holidays.has(previousKey) && holidays.has(nextKey)) {
      holidays.add(currentKey);
    }
  }

  // 日曜の祝日は、直後の祝日ではない日を振替休日にする。
  const sundayHolidays = [...holidays].filter((holiday) => {
    const [holidayYear, month, day] = holiday.split("-").map(Number);
    return new Date(Date.UTC(holidayYear, month - 1, day)).getUTCDay() === 0;
  });
  for (const holiday of sundayHolidays) {
    const [holidayYear, month, day] = holiday.split("-").map(Number);
    let substitute = Date.UTC(holidayYear, month - 1, day) + DAY_MS;
    while (substitute < yearEnd) {
      const date = new Date(substitute);
      const substituteKey = dateKey(year, date.getUTCMonth() + 1, date.getUTCDate());
      if (!holidays.has(substituteKey)) {
        holidays.add(substituteKey);
        break;
      }
      substitute += DAY_MS;
    }
  }

  return holidays;
}

const holidayCache = new Map<number, Set<string>>();

/** 現行の祝日法に基づく日本の祝日判定（2020～2099年）。 */
export function isJapaneseHoliday(date: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (!match) return false;
  const year = Number(match[1]);
  if (year < 2020 || year > 2099) return false;
  let holidays = holidayCache.get(year);
  if (!holidays) {
    holidays = buildHolidaySet(year);
    holidayCache.set(year, holidays);
  }
  return holidays.has(date);
}
